import { color, padding, shadow } from '@evrekadev/evreka-ui-components'
import { Message } from 'ai'
import React from 'react'
import styled from 'styled-components'
import { Button, Typography } from 'components'
import { UilClipboard, UilVolume } from '@iconscout/react-unicons'
import { useTextToSpeech } from 'utils/useTextToSpeech'

export type ChatMessageProps = {
  message: Message
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { content, role } = message
  const { speak, status, stop } = useTextToSpeech()

  const isUser = role === 'user'

  const onSpeakClick = () => {
    if (status === 'speaking') {
      stop()
    } else {
      speak(content)
    }
  }

  return (
    <ChatMessageWrapper isUser={isUser}>
      <Typography fontSize="text_sm" fontWeight="medium" color="grey600">
        {isUser ? 'You' : 'Evreka AI'}
      </Typography>
      <ChatMessageStyled isUser={isUser}>
        <Typography fontSize="text_md" fontWeight={isUser ? 'medium' : 'regular'}>
          {content}
        </Typography>
      </ChatMessageStyled>
      {!isUser && (
        <MessageOptionButtons>
          <Button variant="text" color="secondary" iconLeft={UilClipboard} onlyIcon size="small" />
          <Button
            onClick={onSpeakClick}
            variant="text"
            color="secondary"
            iconLeft={UilVolume}
            onlyIcon
            size="small"
          />
        </MessageOptionButtons>
      )}
    </ChatMessageWrapper>
  )
}

const MessageOptionButtons = styled.div`
  position: absolute;
  top: 4;
  right: 4px;
  display: flex;
  gap: 4px;
  opacity: 0;
`

const ChatMessageWrapper = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 70%;
  width: fit-content;
  align-self: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  align-items: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  position: relative;

  &:hover {
    ${MessageOptionButtons} {
      opacity: 1;
    }
  }
`

const ChatMessageStyled = styled.div<{ isUser: boolean }>`
  ${padding([6, 12])}
  ${shadow('xs')}
  min-width: 2rem;
  border-radius: 12px;
  border-start-end-radius: ${({ isUser }) => (isUser ? '0' : '12px')};
  border-start-start-radius: ${({ isUser }) => (isUser ? '12px' : '0')};
  background-color: ${({ isUser }) => color(isUser ? 'pink25' : 'white')};
  p {
    color: ${({ isUser }) => color(isUser ? 'pink600' : 'grey700')};
  }
  border: 1px solid ${({ isUser }) => color(isUser ? 'pink500' : 'grey200')};
`

export const ChatMessageList = styled.div<{ isEmpty?: boolean }>`
  ${padding([16])}
  background-color: ${color('grey100')};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: ${({ isEmpty }) => (isEmpty ? 'center' : 'flex-end')};
  position: relative;
  min-height: 100%;
`

export const ChatMessageScrollWrapper = styled.div`
  flex-grow: 1;
  overflow: auto;
`
